// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import FancyScroll, { FancyScrollRefProps } from "@ces/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@ces/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@ces/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@ces/App/Components/Notify/Notify";
import { CustomAppState } from "@ces/App/Store/Reducers";
import {
    canSubmitForCustomQuote,
    xCanCheckoutWithCart,
    xIsCheckoutDisabled,
} from "@ces/App/Store/Selectors/GeneralSelectors";
import { CustomSettingsCollectionModel } from "@ces/App/Store/Selectors/SettingsSelectors";
import { LOGIN_OR_QUOTE } from "@ces/XDL/constants";
import { parseBoolean, xSiteMessage } from "@ces/XDL/Utilities";
import { mergeWithCss } from "@ces/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canSubmitForQuote,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
    hasQuoteRequiredProducts,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const isCheckoutDisabled = xIsCheckoutDisabled(state);
    const currentPage = getCurrentPage(state);
    const session = getSession(state);

    return {
        isCheckoutDisabled,
        canSubmitForQuote: canSubmitForQuote(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        shouldLoadCart:
            !getCurrentCartState(state).value &&
            !getCurrentCartState(state).isLoading &&
            currentPage.type !== "CartPage",
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        loginPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        requestQuotePageUrl: getPageLinkByPageType(state, "RequestQuotePage")?.url || "/RequestQuote",
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isLoading: state.data.carts.isLoading,
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe),
        customShowPricing:
            (getSettingsCollection(state) as CustomSettingsCollectionModel)?.customSetting?.showPricing === "Always",
        hasInvalidPriceItems: hasProductsWithInvalidPrice(cart),
        canSubmitCustomQuote: canSubmitForCustomQuote(cart),
        enableColonialCustomQuote: parseBoolean(
            (getSettingsCollection(state) as CustomSettingsCollectionModel)?.customSetting?.enableColonialCustomQuote,
        ),
        customRequestQuotePageUrl: getPageLinkByPageType(state, "RfqCustomRequestQuotePage")?.url || "/CustomRfq",
        canCheckoutWithCart: xCanCheckoutWithCart(state),
        showOutOfStockMessage: parseBoolean(cart?.properties?.pickupHasOutOfStock),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
    setIsPreloadingData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    wrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    noCartLinesNotify?: TypographyProps;
    totalWrapper?: InjectableCss;
    title?: TypographyProps;
    totalItems?: TypographyProps;
    viewCartLink?: LinkPresentationProps;
    cartLinesContainer?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    bottomWrapper?: InjectableCss;
    bottomSectionWrapper?: InjectableCss;
    totalItem?: InjectableCss;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    cantCheckoutCart?: InjectableCss;
    notify?: TypographyProps;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    requestQuotePageUrl,
    isPunchOutOrder,
    isCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    preloadCheckoutShippingData,
    canSubmitForQuote,
    doPunchOutCheckout,
    isUpdatingCartLine,
    isRemovingCartLine,
    isLoading,
    checkoutShippingPageUrl,
    isPopupShown,
    isAuthenticated,
    customShowPricing,
    loginPageUrl,
    hasInvalidPriceItems,
    enableColonialCustomQuote,
    canSubmitCustomQuote,
    customRequestQuotePageUrl,
    canCheckoutWithCart,
    showOutOfStockMessage,
    hasQuoteRequiredProducts,
    setIsPreloadingData,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();
    const productsCannotBePurchased =
        cart?.cartLines?.some(o => o.isRestricted || !o.isActive || o.cantBuy) || hasInvalidPriceItems;
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        // XNG-Changes
        const hasZeroPricingProducts = cart?.cartLines?.some(cartLine => !cartLine.pricing?.unitNetPrice);
        if (!quoteRequiredModalIsOpen && (hasQuoteRequiredProducts || hasZeroPricingProducts)) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        setIsCheckoutLoading(true);
        preloadCheckoutShippingData({
            onSuccess: () => {
                setIsPreloadingData({ isPreloadingData: false });
                setIsCheckoutLoading(false);
                history.push(checkoutShippingPageUrl!);
            },
        });
    };

    const requestQuoteHandler = () => {
        hideTippy();
        history.push(enableColonialCustomQuote ? customRequestQuotePageUrl : requestQuotePageUrl);
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    const loginHandler = () => {
        hideTippy();
        history.push(loginPageUrl || "/MyAccount/SignIn");
    };

    if (!cart && isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];
    const isLoadingCart = isLoading[cart.id];
    const isCheckoutButtonDisabled =
        isCheckoutDisabled || isCheckoutLoading || isLoadingCart || productsCannotBePurchased;
    if (!cartLines?.length) {
        return (
            <StyledWrapper {...mergeWithCss(styles.wrapper, styles.notifyWrapper)}>
                <Notify {...styles.noCartLinesNotify} type="info" showClose={false}>
                    {siteMessage("Custom_MiniCart_EmptyMessage")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isLoadingCart || isUpdatingCartLine || isRemovingCartLine}>
            <StyledWrapper {...styles.wrapper}>
                <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                    <StyledWrapper
                        {...styles.cartLinesWrapper}
                        ref={parentBoxRef}
                        data-test-selector="cartLines-container"
                    >
                        {productsCannotBePurchased && (
                            <StyledWrapper {...styles.cantCheckoutCart}>
                                <Notify
                                    {...styles.notify}
                                    showClose={false}
                                    type="warning"
                                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                                >
                                    {siteMessage("Cart_ProductsCannotBePurchased")}
                                </Notify>
                            </StyledWrapper>
                        )}
                        {showOutOfStockMessage && (
                            <StyledWrapper {...styles.cantCheckoutCart}>
                                <Notify type="warning" {...styles.notify} showClose={false}>
                                    {siteMessage("Custom_Cart_Pickup_OOS_Message")}
                                </Notify>
                            </StyledWrapper>
                        )}

                        <StyledWrapper {...styles.cartLinesContainer}>
                            {cartLines?.map(cartLine => {
                                const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                return (
                                    <MiniCartLineCard
                                        key={`miniCartLine_${cartLine.id}`}
                                        cartLine={cartLine}
                                        cart={cart}
                                        hideTippy={hideTippy}
                                        qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                        removeCartLineClickHandler={removeCartLineClickHandler}
                                        errorMessages={errorMessages}
                                        canSeePrices={customShowPricing || isAuthenticated}
                                    />
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </FancyScroll>
                <StyledWrapper {...styles.bottomWrapper}>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalValue}>{totalCountDisplay}</Typography>
                            <Typography {...styles.totalLabel}>{translate("Items")}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{`${translate("Subtotal")}:`}</Typography>
                            <Typography {...styles.totalValue}>
                                {!isAuthenticated && !customShowPricing
                                    ? xSiteMessage("Custom_Cart_LogIn_Or_Quote") || translate(LOGIN_OR_QUOTE)
                                    : orderSubTotalDisplay}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        <Button {...styles.viewCartButton} onClick={handleViewCarClick}>
                            {translate("View Cart")}
                        </Button>
                        {isAuthenticated &&
                        (canCheckoutWithCart || !enableColonialCustomQuote || productsCannotBePurchased) ? (
                            <Button
                                {...styles.checkoutButton}
                                onClick={checkoutHandler}
                                disabled={isCheckoutButtonDisabled}
                                aria-label={
                                    cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")
                                }
                            >
                                {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                            </Button>
                        ) : (
                            <Button
                                {...styles.checkoutButton}
                                disabled={enableColonialCustomQuote ? !canSubmitCustomQuote : !canSubmitForQuote}
                                onClick={requestQuoteHandler}
                                data-test-selector="cartTotal_Request_A_Quote"
                            >
                                {translate("Request a Quote")}
                            </Button>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <TwoButtonModal
                headlineText=""
                modalIsOpen={quoteRequiredModalIsOpen}
                messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={() => setQuoteRequiredModalIsOpen(false)}
                onSubmit={checkoutHandler}
                submitTestSelector="QuoteInCartMessageSubmit"
                dataTestSelector="twoButtonModal_RequiresQuote"
            ></TwoButtonModal>
        </LoadingOverlay>
    );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
